import React, { useEffect, useState } from 'react';

//Hooks
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';

// Component
import UpdateContactsNew from '~/components/Contacts/UpdateContactsNew';
import CustomFields, { getCustomFieldName } from '~/components/Contacts/UpdateContactsNew/CustomFields';
import EventLog from '~/components/Contacts/UpdateContactsNew/EventLog';
import MailingTabs from '~/components/Contacts/MailingTab';
import PropertyTab from '../PropertyTab';
import Notes from '~/components/Contacts/Notes';
import ContactDetailsSkeleton from '~/components/Contacts/ContactDetailsSkeleton';
import ContactsMeta from '~/components/Contacts/ContactsMeta';
import Engagement from '~/components/Contacts/ContactDetails/Engagement';

// Utils
import request from '~/utils/request';
import { basicDetails } from '~/utils/contact-detail-coulmns';
import { MESSAGES } from '~/utils/message';
import {
  DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
  EVENT_TYPES,
  EVENT_TYPES_KEY,
} from '~/utils/constants';

// Components
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcButton from '~/components/General/OlcButton';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';

// MUI Components
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  GlobalStyles,
} from '@mui/material';

// Icons
import {
  EditNoteCIcon,
  DeleteNoteCIcon,
  ExportModalCIcon,
  ContactDetailsDeleteCIcon,
  ContactInfoCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';
import { updateContactCustomFields } from '~/redux/actions/contacts-actions';

const ContactDetails = () => {
  const [logs, setLogs] = useState([]);
  const [eventType, setEventType] = useState(EVENT_TYPES[0]);
  const [orignalContactData, setOrignalContactData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logLoading, setlogLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE);
  const [activeTab, setActiveTab] = useState(0);
  const [propertyAddress, setPropertyAddress] = useState({
    address: '',
    state: '',
    city: '',
    zip: '',
    offer: '',
  });
  const [customFields, setCustomFields] = useState([]);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allCustomFields = useSelector((state) => state?.customFieldsReducers?.allCustomFields);

  const isPropertyAddressEnabled = useSelector(
    (state) =>
      state?.generalSettingsReducers?.generalSettings?.propertyAddressStrictness === 'Include' ||
      false
  );

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const contactId = params.id;

  const modifiedContactsFields = orignalContactData?.meta?.custom_fields?.reduce(
    (result, field) => {
      const [key] = Object.keys(field);
      result[key] = field[key];
      return result;
    },
    {}
  );

  const handleOpenDelete = () => {
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      message: MESSAGES.CONTACTS.DELETE.MESSAGE,
      handleOk: () => {
        handleDeleteContact();
      },
      successButtonName: 'Yes',
      loading: false,
      header: 'Delete Contact',
      icon: ContactDetailsDeleteCIcon,
      compactActions: true,
      className: '',
      modalName: '',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const openExportModal = () => {
    setOpenConfirmModal({
      type: 'export',
      open: true,
      message: MESSAGES.CONTACTS.EXPORT_SINGLE_CONTACTS,
      description: MESSAGES.CONTACTS.DETAILS_EXPORT_SINGLE_CONTACT_DESCRIPTION,
      successButtonName: 'Export',
      header: 'Export Contact',
      svg: <ExportModalCIcon fill="#ed5c2f" />,
      compactActions: true,
      className: 'exportModalContact',
      modalName: '',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
      handleOk: () => {
        exportContacts();
      },
    });
  };

  const exportContacts = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await request.get(`contacts/export?contactIds=[${contactId}]`);
      dispatch(success(response.data.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }, 2000);
    }
  };

  const fetchContactData = async (loader = true) => {
    try {
      loader ? setIsLoading(true) : undefined;
      const response = await request.get(`contacts/${contactId}`, {});
      if (response.status === 200) {
        setOrignalContactData(response.data.data);
        response?.data?.data?.properties?.length
          ? setPropertyAddress({
              address: response?.data?.data?.properties[0]?.address1,
              state: response?.data?.data?.properties[0]?.state,
              city: response?.data?.data?.properties[0]?.city,
              zip: response?.data?.data?.properties[0]?.zip,
              offer: response?.data?.data?.properties[0]?.rosEstimateValue,
            })
          : undefined;
      }
    } catch (error) {
      navigate('/contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContactLogs = async () => {
    try {
      setlogLoading(true);
      const response = await request.get(`contacts/logs/${contactId}`, {
        filter: EVENT_TYPES_KEY.find(
          (item) => item.title === eventType?.title
        )?.value?.toLowerCase(),
        ...pagination,
      });
      if (response.status === 200) {
        setLogs(response.data.data);
      }
    } catch (error) {
      return error;
    } finally {
      setlogLoading(false);
    }
  };

  const handleDeleteContact = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await request.delete(`contacts/${contactId}`);
      dispatch(success(response.data.message));
      navigate('/contacts');
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }, 2000);
    }
  };

  const updateCustomFields = async () => {
    try {
      const payload = customFields.reduce((result, field) => {
        const key = getCustomFieldName(field);
        if (key) {
          result[key] = field[key];
        }
        return result;
      }, {});

      const response = await dispatch(
        updateContactCustomFields(contactId, {
          customFields: payload,
        })
      );
      if (response?.status === 200) {
        return true;
      }
      dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      return false;
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return false;
    }
  };

  const handleSaveSuccess = async () => {
    // Immediately set loading and clear data to prevent flicker when save succeeds
    setOrignalContactData(null);
    await fetchContactData(false);
    setIsEditing(false);
  };

  useEffect(() => {
    fetchContactLogs();
  }, [eventType, pagination]);

  useEffect(() => {
    fetchContactData(false);
  }, [isEditing]);

  useEffect(() => {
    const fields = allCustomFields.map((allFields) => {
      return {
        [allFields.value]:
          modifiedContactsFields && Object.keys(modifiedContactsFields).length
            ? modifiedContactsFields[allFields?.value]
            : '',
      };
    });
    setCustomFields(fields);
  }, [
    JSON.stringify(allCustomFields),
    JSON.stringify(orignalContactData),
    JSON.stringify(modifiedContactsFields),
  ]);

  const renderTags = (tags) => {
    if (tags.length > 2) {
      return (
        <Box className="tooltipTagsContainer">
          {tags.slice(2).map((tag, index) => (
            <Box className="tooltipTags" key={index}>
              <Chip className="tooltipTag" label={tag} />
            </Box>
          ))}
        </Box>
      );
    }
  };

  // useEffect(() => {
  //   const body = document.body;
  //   if (window.location.pathname.includes(`/contacts/${contactId}`)) {
  //     body.style.backgroundColor = '#F9FAFB';
  //   }
  //   return () => {
  //     body.style.backgroundColor = 'inherit';
  //   };
  // }, []);

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Container
        maxWidth={false}
        className="contactDetailsPage"
        sx={{
          backgroundColor: '#F9FAFB',
          padding: 0,
          minHeight: 'calc(100vh - 95px)',
        }}
      >
        {!isEditing ? (
          <Grid container className="detailsGrid">
            <Box className="detailsHeader">
              <OlcPageBackButton onClick={() => navigate('/contacts')}>
                Back to Contacts
              </OlcPageBackButton>

              <Box className="contactDetailsAction">
                <OlcButton
                  variant="secondary"
                  className="contactActionButton contactDeleteButton"
                  onClick={handleOpenDelete}
                  startIcon={<DeleteNoteCIcon />}
                >
                  {MESSAGES.CONTACTS.DELETE_BTN_TEXT}
                </OlcButton>
                <OlcButton
                  variant="secondary"
                  className="contactActionButton"
                  onClick={openExportModal}
                  startIcon={<ExportModalCIcon fill="currentColor" />}
                >
                  {MESSAGES.CONTACTS.EXPORT_BTN_TEXT}
                </OlcButton>
                <OlcButton
                  variant="secondary"
                  className="contactActionButton"
                  onClick={handleEditClick}
                  startIcon={<img src={EditNoteCIcon} alt="" />}
                >
                  {MESSAGES.CONTACTS.EDIT_BTN_TEXT}
                </OlcButton>
              </Box>
            </Box>

            <Grid container columnSpacing={4} rowSpacing={4} className="detailsColumns">
              <Grid item lg={8} md={12} sm={12} xs={12} className="detailsMainColumn">
                <Card className="detailsCard">
                  <CardContent>
                    <Box className="detailsCardHeader">
                      <Typography className="contactDetailsTitle">
                        {MESSAGES.CONTACTS.CONTACT_DETAIL_TITLE}
                      </Typography>
                    </Box>
                    <Box className="detailsGridLayout">
                      {isLoading ? (
                        <ContactDetailsSkeleton variant="grid" rows={3} columns={2} />
                      ) : (
                        <Box className="detailsRow">
                          {basicDetails(orignalContactData).map((item, index) => {
                            return (
                              <Box key={index} className="detailsColumn">
                                {item?.attribute === 'Tags' && item?.value?.length > 0 ? (
                                  <>
                                    <Typography className="detailsAttribute">
                                      {item?.attribute}
                                    </Typography>
                                    <Box className="detailsTags">
                                      {item?.value?.slice(0, 2).map((tags) => (
                                        <Box className="tagContent" key={tags}>
                                          <Chip className="tagChip" label={tags} />
                                        </Box>
                                      ))}
                                      {item?.value?.length > 2 && (
                                        <Box className="tagContent">
                                          <Tooltip
                                            title={renderTags(item?.value)}
                                            placement="bottom-start"
                                            componentsProps={{
                                              tooltip: {
                                                sx: {
                                                  padding: '8px !important',
                                                },
                                              },
                                            }}
                                          >
                                            <Chip
                                              className="tagChip"
                                              sx={{ cursor: 'pointer' }}
                                              label={`${item?.value?.length - 2} More Tags...`}
                                            />
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    <Typography className="detailsAttribute">
                                      {item?.attribute}
                                    </Typography>
                                    <Typography className="detailsValue">
                                      {item?.value || '-'}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Box className="tabsContainer">
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className="contactTabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{
                      className: 'tabIndicator',
                    }}
                  >
                    <Tab
                      label={
                        <Box className="tabLabel">
                          <>Mailing Address</>
                          <Tooltip
                            title={MESSAGES.CONTACTS.MAILING_ADDRESS_TOOLTIP}
                            placement="bottom-start"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '12px',
                                  maxWidth: '196px',
                                  padding: '8px !important',
                                },
                              },
                            }}
                          >
                            <img
                              src={ContactInfoCIcon}
                              alt="Mailing Address"
                              style={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </Box>
                      }
                      className="contactTab"
                    />
                    <Tab
                      label={
                        <Box className="tabLabel">
                          <>Property Address</>
                          <Tooltip
                            title={MESSAGES.CONTACTS.PROPERTY_ADDRESS_TOOLTIP}
                            placement="bottom-start"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '12px',
                                  maxWidth: '182px',
                                  padding: '8px !important',
                                },
                              },
                            }}
                          >
                            <img
                              src={ContactInfoCIcon}
                              alt="Property Address"
                              style={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </Box>
                      }
                      className="contactTab"
                    />
                    <Tab label="Custom Fields" className="contactTab" />
                    {/* <Tab label="Engagement" className="contactTab" /> */}
                    <Tab label="Event Logs" className="contactTab" />
                  </Tabs>
                </Box>
                <Card className="tabbedCard">
                  <CardContent className="tabContent">
                    {/* Mailing Tab Content */}
                    {activeTab === 0 &&
                      (isLoading ? (
                        <ContactDetailsSkeleton variant="mailing-tab" />
                      ) : (
                        <MailingTabs orignalContactData={orignalContactData} />
                      ))}

                    {/* Property Tab Content */}
                    {activeTab === 1 &&
                      (isLoading ? (
                        <ContactDetailsSkeleton variant="property-tab" />
                      ) : (
                        <PropertyTab propertyAddress={propertyAddress} isEditing={isEditing} />
                      ))}

                    {/* Custom Fields Tab Content */}
                    {activeTab === 2 &&
                      (isLoading ? (
                        <ContactDetailsSkeleton variant="custom-fields" />
                      ) : (
                        <Box className="customFieldsTabContent">
                          <CustomFields
                            isEditing={isEditing}
                            customFields={customFields}
                            setCustomFields={setCustomFields}
                          />
                        </Box>
                      ))}

                    {/* Engagement Tab Content */}
                    {/* {activeTab === 3 &&
                      (isLoading ? (
                        <ContactDetailsSkeleton variant="engagement-tab" />
                      ) : (
                        <Box className="engagementTabContent">
                          <Engagement />
                        </Box>
                      ))} */}

                    {/* Event Logs Tab Content */}
                    {activeTab === 3 && (
                      <Box className="eventLogsTabContent">
                        <EventLog
                          data={logs}
                          eventType={eventType}
                          setEventType={setEventType}
                          loading={logLoading}
                          mainLoader={isLoading}
                          updatePagination={setPagination}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item lg={4} md={12} sm={12} xs={12} className="detailsSidebarColumn">
                <Box className="detailsSidebar">
                  {/* Contacts Meta */}
                  <ContactsMeta isLoading={isLoading} orignalContactData={orignalContactData} />

                  {/* Notes */}
                  <Notes isLoading={isLoading} contactId={contactId} />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <UpdateContactsNew
            propertyAddress={propertyAddress}
            setPropertyAddress={setPropertyAddress}
            customFields={customFields}
            setCustomFields={setCustomFields}
            isPropertyAddressEnabled={isPropertyAddressEnabled}
            setIsEditing={setIsEditing}
            handleSaveSuccess={handleSaveSuccess}
            setIsLoading={setIsLoading}
            fetchContactLogs={fetchContactLogs}
            modifiedContactsFields={modifiedContactsFields}
            logs={logs}
            eventType={eventType}
            setEventType={setEventType}
            logLoading={logLoading}
            mainLoader={isLoading}
            updatePagination={setPagination}
            orignalContactData={orignalContactData}
            isEditing={isEditing}
            updateCustomFields={updateCustomFields}
            setDetailsActiveTab={setActiveTab}
          />
        )}
        {/* Export Modal */}
        {openConfirmModal.type === 'export' && (
          <ConfirmModal openConfirmModal={openConfirmModal} type="export" />
        )}
        {/* Delete Modal */}
        {openConfirmModal.type === 'delete' && <ConfirmModal openConfirmModal={openConfirmModal} />}
      </Container>
    </>
  );
};

export default ContactDetails;
